// @flow

import React, { memo, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { p2pLog } from '../../logger'
import { p2pStatsCollector } from '../../debug/collector'
import { commitP2PStats, resetP2PStats } from '../../models/actions'
import { getP2PStats } from '../../models/selectors'

import { Container, Title, Note, StatGroup, GroupLabel, StatRow, Swatch, Row, LagValue } from './P2PStatsOverlay.style'

const POLL_INTERVAL = 1000
const LIVE_LAG_OK = 8

const COLORS = {
  download: '#ff1745',
  http: '#faf21b',
  p2p: '#ff7f0e',
  upload: '#ADD8E6',
}

const convertToMbit = (bytes: number): number => (bytes * 8) / 1000000
const convertToMiB = (bytes: number): number => bytes / 1024 / 1024
const calculatePercentage = (part: number, total: number): number | string => {
  if (total === 0) {
    return 0
  }

  return ((part / total) * 100).toFixed(2)
}

type P2PStatsOverlayProps = {
  scale?: number,
}

// eslint-disable-next-line complexity
const P2PStatsOverlayPure = ({ scale }: P2PStatsOverlayProps) => {
  const dispatch = useDispatch()
  const { bound, peerCount, totals } = useSelector(getP2PStats)
  const [speeds, setSpeeds] = useState({
    download: 0,
    http: 0,
    p2p: 0,
    upload: 0,
  })
  const [isLive, setIsLive] = useState(null)
  const [liveLag, setLiveLag] = useState(null)
  const prevBytesRef = useRef({
    http: 0,
    p2p: 0,
    upload: 0,
  })

  useEffect(() => {
    // eslint-disable-next-line complexity
    const intervalId = setInterval(() => {
      const snapshot = p2pStatsCollector.getSnapshot()
      const { bytes } = snapshot
      const prev = prevBytesRef.current

      setSpeeds({
        download: convertToMbit(bytes.http + bytes.p2p - prev.http - prev.p2p),
        http: convertToMbit(bytes.http - prev.http),
        p2p: convertToMbit(bytes.p2p - prev.p2p),
        upload: convertToMbit(bytes.upload - prev.upload),
      })
      prevBytesRef.current = bytes

      dispatch(
        commitP2PStats({
          bound: snapshot.bound,
          peerCount: snapshot.peerCount,
          totals: {
            total: convertToMiB(bytes.http + bytes.p2p),
            http: convertToMiB(bytes.http),
            p2p: convertToMiB(bytes.p2p),
            upload: convertToMiB(bytes.upload),
          },
        }),
      )

      const player = p2pStatsCollector.getPlayer()

      try {
        if (player) {
          const live = player.isLive()

          setIsLive(live)
          setLiveLag(live ? player.getStats()?.liveLatency ?? null : null)
        }
      } catch (err) {
        p2pLog('[P2P][Overlay] live stats read failed:', err)
      }
    }, POLL_INTERVAL)

    return () => {
      clearInterval(intervalId)

      // Drop the last committed snapshot so a later session does not start from stale stats
      dispatch(resetP2PStats())
    }
  }, [dispatch])

  // eslint-disable-next-line no-nested-ternary
  const streamLabel = isLive === null ? '—' : isLive ? 'LIVE' : 'VOD'

  if (!bound) {
    return (
      <Container scale={scale}>
        <Title>P2P DEBUG ○</Title>
        <Note>P2P is not active</Note>
      </Container>
    )
  }

  const totalsStats = [
    { color: COLORS.download, text: `Download — ${totals.total.toFixed(2)} MiB` },
    {
      color: COLORS.http,
      text: `HTTP — ${totals.http.toFixed(2)} MiB — ${calculatePercentage(totals.http, totals.total)}%`,
    },
    {
      color: COLORS.p2p,
      text: `P2P — ${totals.p2p.toFixed(2)} MiB — ${calculatePercentage(totals.p2p, totals.total)}%`,
    },
    { color: COLORS.upload, text: `Upload P2P — ${totals.upload.toFixed(2)} MiB` },
  ]

  const speedStats = [
    { color: COLORS.download, text: `Download — ${speeds.download.toFixed(2)} Mbit/s` },
    { color: COLORS.http, text: `HTTP — ${speeds.http.toFixed(2)} Mbit/s` },
    { color: COLORS.p2p, text: `P2P — ${speeds.p2p.toFixed(2)} Mbit/s` },
    { color: COLORS.upload, text: `Upload P2P — ${speeds.upload.toFixed(2)} Mbit/s` },
  ]

  return (
    <Container scale={scale}>
      <Title>P2P DEBUG ●</Title>

      <Row>
        <span>Stream</span>
        <span>{streamLabel}</span>
      </Row>

      {isLive && (
        <Row>
          <span>Live lag</span>

          <LagValue ok={liveLag != null && liveLag > LIVE_LAG_OK}>
            {liveLag != null ? `${liveLag.toFixed(1)} s` : '—'}
          </LagValue>
        </Row>
      )}

      <Row>
        <span>Peers</span>
        <span>{peerCount}</span>
      </Row>

      <StatGroup>
        <GroupLabel>Totals (MiB)</GroupLabel>

        {totalsStats.map(item => (
          <StatRow key={item.text}>
            <Swatch color={item.color} />

            <span>{item.text}</span>
          </StatRow>
        ))}
      </StatGroup>

      <StatGroup>
        <GroupLabel>Speed (Mbit/s)</GroupLabel>

        {speedStats.map(item => (
          <StatRow key={item.text}>
            <Swatch color={item.color} />

            <span>{item.text}</span>
          </StatRow>
        ))}
      </StatGroup>
    </Container>
  )
}

export const P2PStatsOverlay = memo<P2PStatsOverlayProps>(P2PStatsOverlayPure)

export default P2PStatsOverlay
